/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */

/* eslint-disable sonarjs/cognitive-complexity */
import React, { useCallback, SetStateAction, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { Icons } from '../../../../Shared/Constants/Icons';
import {
  convertToReadableFormat,
  getAcronymName,
  getStoredCustomerDetails,
  AssignmentCardType,
  formatDate,
  getAuthDetails,
  NUMBER_OF_ITEMS_PER_PAGE,
  getFormatDate,
  hasAccessToConversations,
} from '../../../../Shared/Utilities/utils';
import { SearchFieldContainer, SearchInput } from '../../../../Shared/Components/Layout/styles';
import { ITeamMemberData } from './JobAssignmentCard';
import WorkOrdersApiService from '../../Services/WorkOrdersService';
import { CreateWorkOrderTeam } from '../../../../API';
import SnackbarMessage from '../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';
import {
  AssignTeamDiv,
  buttonStyles,
  CardContainerWrapper,
  CardTitle,
  DatePickerContainer,
  HeadingText,
  LogoWrapper,
  TeamCard,
  TeamContainer,
} from './OneTimeJobDetails.Style';
import theme from '../../../../Shared/Themes/theme';
import DateSelection from '../../../../Shared/Components/Common/DatePicker/DateSelection';
import { FieldNames } from '../../Models/AddEditOneTimeJob.Model';
import { useForm } from 'react-hook-form';
import ModifyPopUp from './ModifyPopUp';
import { isEmpty } from 'lodash';
import { IConversationContext } from '../PostJobAwardConversations/PostJobAwardConversations';
import { UserType } from '../../../../Shared/Constants/App';
import VendorApiService from '../../../../Shared/Services/VendorService';
import { useNavigate } from 'react-router-dom';
import { StyledPagination } from '../../../../Shared/Components/Common/CommonStyle/Pagination.Style';
import moment from 'moment';

interface ShiftDetails {
  shiftDetailId: string;
  startTime: string;
  endTime: string;
  workOrderId?: string;
  punchInGraceMinutes?: number | null;
  punchOutGraceMinutes?: number | null;
  scheduleStartDate: any;
  scheduleEndDate: any;
}
interface AssignTeam {
  teamData: ITeamMemberData[];
  shiftTimings: ShiftDetails[];
  setTeamData: (data: ITeamMemberData[]) => void;
  originalTeamData: ITeamMemberData[];
  setOriginalTeamData: (data: ITeamMemberData[]) => void;
  associateTeamData: ITeamMemberData[];
  setAssociateTeamData: (data: ITeamMemberData[]) => void;
  originalAssociateTeamData: ITeamMemberData[];
  setOriginalAssociateTeamData: (data: ITeamMemberData[]) => void;
  checkedSupervisors: ITeamMemberData[];
  setCheckedSupervisors: (data: ITeamMemberData[]) => void;
  checkedAssociates: ITeamMemberData[];
  setCheckedAssociates: (data: ITeamMemberData[]) => void;
  isTeamDataLoading: boolean;
  facilityId: string;
  workOrderId: string;
  onClose: (type: AssignmentCardType) => void;
  isViewOnly: boolean;
  assignmentType: AssignmentCardType;
  workOrderData: any;
  shiftDetailId: string;
  renderAssignedSupervisorTeamData: (shiftId: string) => void;
  renderAssignedAssociateTeamData: (shiftId: string) => void;
  getWorkOrderDetails: () => Promise<void>;
  fetchDisableStartJobResult: any;
  fetchScheduleDetails: (workOrderId: string) => Promise<void>;
  jobData: any;
  totalCount?: number;
  fetchOrganizationUsers?: (pageNumber: number, searchString: string) => void;
  // Selected shift dates (ISO yyyy-mm-dd) for SP self-perform date-picked (Custom) schedules.
  customDates?: string[];
}

export const checkAlreadySelected = (data: ITeamMemberData[], memberId: string): boolean => {
  return data.some((prevMember) => prevMember.id === memberId);
};

export interface AssociateShiftDateProps {
  associateStartDate: string;
  associateEndDate: string;
  userId: string;
}

const AssignTeam = ({
  teamData,
  setTeamData,
  originalTeamData,
  setOriginalTeamData,
  associateTeamData,
  setAssociateTeamData,
  originalAssociateTeamData,
  setOriginalAssociateTeamData,
  checkedSupervisors,
  setCheckedSupervisors,
  checkedAssociates,
  setCheckedAssociates,
  isTeamDataLoading,
  workOrderId,
  onClose,
  assignmentType,
  workOrderData,
  shiftDetailId,
  shiftTimings,
  fetchScheduleDetails,
  jobData,
  totalCount,
  fetchOrganizationUsers,
  customDates,
}: AssignTeam) => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [isRecommended, setIsRecommended] = useState(true);
  const [teamCardNumber, setTeamCardNumber] = useState<number>(0);
  const [isConfirmButtonLoading, setIsConfirmButtonLoading] = useState<boolean>(false);
  const [isDataSaving, setIsDataSaving] = useState<boolean>(false);
  const [snackbarStyle, setSnackBarStyle] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { t } = useTranslation(['assignJob', 'oneTimeJob', 'calendar']);
  const { divider, buttonText, createJobButton, cancelButton } = buttonStyles;
  const [editAssociateShiftDate, setEditAssociateShiftDate] = useState<AssociateShiftDateProps>({
    userId: '',
    associateStartDate: '',
    associateEndDate: '',
  });

  const [conversationContext, setConversationContext] = useState<IConversationContext>({} as IConversationContext);

  const [loadingMembers, setLoadingMembers] = useState<Record<string, boolean>>({});

  const selectedShift = shiftTimings.filter((data) => data?.shiftDetailId === shiftDetailId);

  const { control, getValues, reset } = useForm({
    defaultValues: {
      startDate: selectedShift?.[0]?.scheduleStartDate
        ? getFormatDate(selectedShift?.[0]?.scheduleStartDate as string)
        : null,
      endDate: selectedShift?.[0]?.scheduleEndDate
        ? getFormatDate(selectedShift?.[0]?.scheduleEndDate as string)
        : null,
    },
  });

  const storedAuthDetails = getAuthDetails();

  const { userId = '' } = getStoredCustomerDetails();

  const loggedInUserInternalId = storedAuthDetails['custom:UserId'] ?? userId;
  const loggedInUserName = storedAuthDetails['name'];

  const navigate = useNavigate();

  useEffect(() => {
    if (editAssociateShiftDate?.associateStartDate || editAssociateShiftDate?.associateEndDate) {
      reset({
        startDate: editAssociateShiftDate?.associateStartDate
          ? getFormatDate(editAssociateShiftDate?.associateStartDate)
          : getFormatDate(workOrderData?.actualStartDate as string),
        endDate: editAssociateShiftDate?.associateEndDate
          ? getFormatDate(editAssociateShiftDate?.associateEndDate)
          : getFormatDate(workOrderData?.actualEndDate as string),
      });
    }
  }, [editAssociateShiftDate, reset, workOrderData]);

  const handleSelectMember = (memberId: string, memberType: string) => {
    if (memberType === 'supervisor') {
      handleSelectSupervisor(memberId);
    } else {
      handleSelectAssociate(memberId);
    }
  };

  const handleSelectSupervisor = (memberId: string) => {
    const teamMembersData = [...teamData];
    const originalTeamMembersData = [...originalTeamData];
    const selectedMember = teamMembersData.find((member: ITeamMemberData) => member.id === memberId);
    if (selectedMember) {
      selectedMember.isChecked = !selectedMember?.isChecked;
      const selectedMemberindex = teamMembersData.findIndex((member: ITeamMemberData) => member.id === memberId);
      const originalSelectedMemberindex = originalTeamMembersData.findIndex(
        (member: ITeamMemberData) => member.id === memberId
      );

      if (selectedMemberindex !== -1) {
        teamMembersData[selectedMemberindex] = selectedMember;
        setTeamData(teamMembersData);
      }
      if (originalSelectedMemberindex !== -1) {
        originalTeamMembersData[originalSelectedMemberindex] = selectedMember;
        setOriginalTeamData(originalTeamMembersData);
      }
    }
    const selectedTeamMember = checkedSupervisors.find((member: any) => member.id === memberId); //Checking whether user is already selected.
    let modifiedSelectedList: any = [...checkedSupervisors];
    if (isEmpty(selectedTeamMember)) {
      const currentlySelectedTeamMember = originalTeamMembersData?.find((member: any) => member.id === memberId) ?? {};
      modifiedSelectedList.push(currentlySelectedTeamMember);
    } else {
      modifiedSelectedList = modifiedSelectedList.map((selectedMember: any) => {
        if (selectedMember?.id === memberId) {
          return { ...selectedMember, isChecked: !selectedMember?.isChecked };
        }
        return selectedMember;
      });
    }
    setCheckedSupervisors(modifiedSelectedList || []);
  };

  const handleSelectAssociate = (memberId: string) => {
    const teamMembersData = [...associateTeamData];
    const originalTeamMembersData = [...originalAssociateTeamData];
    const selectedMember = teamMembersData.find((member: ITeamMemberData) => member.id === memberId);
    if (selectedMember) {
      selectedMember.isChecked = !selectedMember?.isChecked;
      const selectedMemberindex = teamMembersData.findIndex((member: ITeamMemberData) => member.id === memberId);
      const originalSelectedMemberindex = originalTeamMembersData.findIndex(
        (member: ITeamMemberData) => member.id === memberId
      );

      if (selectedMemberindex !== -1) {
        teamMembersData[selectedMemberindex] = selectedMember;
        setAssociateTeamData(teamMembersData);
      }
      if (originalSelectedMemberindex !== -1) {
        originalTeamMembersData[originalSelectedMemberindex] = selectedMember;
        setOriginalAssociateTeamData(originalTeamMembersData);
      }
    }
    const selectedTeamMember = checkedAssociates.find((member: any) => member.id === memberId); //Checking whether user is already selected.
    let modifiedSelectedList: any = [...checkedAssociates];
    if (isEmpty(selectedTeamMember)) {
      const currentlySelectedTeamMember = originalTeamMembersData?.find((member: any) => member.id === memberId) ?? {};
      modifiedSelectedList.push(currentlySelectedTeamMember);
    } else {
      modifiedSelectedList = modifiedSelectedList.map((selectedMember: any) => {
        if (selectedMember?.id === memberId) {
          return { ...selectedMember, isChecked: !selectedMember?.isChecked };
        }
        return selectedMember;
      });
    }
    setCheckedAssociates(modifiedSelectedList || []);
  };

  const handleDeleteTeam = async (associateDetails: ITeamMemberData | undefined) => {
    try {
      setIsLoading(true);
      const userId = associateDetails?.userId ?? '';
      const deleteAssociateResponse = await WorkOrdersApiService.deleteTeamAssignmentWorkOrderTeam({
        workOrderId,
        userIds: [userId],
        woScheduleMasterId: shiftDetailId,
      });
      console.log('Team member deleted successfully', deleteAssociateResponse);
      handleCloseModal();
      const message =
        assignmentType === AssignmentCardType.AssignSupervisor
          ? t('assignJob:successfullyDeletedSupervisor')
          : t('assignJob:successfullyDeletedAssociate');
      setSnackBarStyle(true);
      setSnackbarMessage(message);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setTimeout(() => {
        onClose(assignmentType);
      }, 1000);
      await fetchScheduleDetails(workOrderId);
    } catch (error) {
      console.error('Failed to delete team member', error);
    } finally {
      setIsLoading(false);
      setSnackBarStyle(false);
    }
  };

  const onConfirmSupervisors = async () => {
    try {
      setIsDataSaving(true);
      const customerDetails = getStoredCustomerDetails();
      const vendorId = customerDetails.customerId;
      const newlyAddedSupervisors = checkedSupervisors.filter((item) => !item.isAlreadyChecked && item.isChecked);
      const createWorkOrderTeamInput: CreateWorkOrderTeam[] = newlyAddedSupervisors.map((item) => {
        if (item.teamId) {
          return {
            userId: item?.id || '',
            vendorId,
            roleId: item?.roleId as string,
            isSupervisor: item?.isSupervisor,
            teamId: item?.teamId,
          };
        }
        return {
          userId: item?.id || '',
          vendorId,
          roleId: item?.roleId as string,
          isSupervisor: item?.isSupervisor,
        };
      });
      const confirmedTeamResponse = await WorkOrdersApiService.addTeamAssignmentWorkOrderTeam({
        workOrderId,
        createScheduleAssignmentsInput: createWorkOrderTeamInput,
        woScheduleMasterId: shiftDetailId,
      });
      if (confirmedTeamResponse.errors?.length) {
        setSnackBarStyle(true);
        setSnackbarMessage(confirmedTeamResponse.errors[0].message as SetStateAction<string>);
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsDataSaving(false);
      } else {
        setSnackBarStyle(true);
        setSnackbarMessage(`${t('messages:successfullyAssignedSupervisor')}`);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        setIsDataSaving(false);
        setTimeout(() => {
          onClose(assignmentType);
        }, 1000);
        await fetchScheduleDetails(workOrderId);
      }
    } catch (e) {
      setIsDataSaving(false);
      console.log('Error:', e);
      setSnackBarStyle(false);
    }
  };

  const handleNextButton = () => {
    if (assignmentType !== AssignmentCardType.AssignSupervisor) {
      setTeamCardNumber(teamCardNumber + 1);
      setEditAssociateShiftDate({
        userId: '',
        associateStartDate: '',
        associateEndDate: '',
      });
    } else {
      if (assignmentType === AssignmentCardType.AssignSupervisor) {
        onConfirmSupervisors();
      }
    }
  };

  const [isModalOpen, setModalOpen] = useState(false);
  const [deleteMember, setDeleteMember] = useState<ITeamMemberData>();

  const handleOpenModal = (member: ITeamMemberData) => {
    setDeleteMember(member);
    setModalOpen(true);
  };
  const handleCloseModal = () => setModalOpen(false);

  const handleConversationIconClick = useCallback(() => {
    const queryParams = new URLSearchParams({
      jobId: jobData?.jobDetails?.jobId,
    });

    navigate(`/post-job-award-conversations?${queryParams.toString()}`, {
      state: {
        ...jobData?.jobDetails,
        conversationContext: conversationContext,
      },
    });
  }, [conversationContext, jobData]);

  const setDataForChat = useCallback(async (selectedChat: ITeamMemberData) => {
    if (!selectedChat.id) {
      return;
    }
    try {
      setLoadingMembers((prev) => ({
        ...prev,
        [selectedChat.id as string]: true,
      }));
      const { customerId = '' } = getStoredCustomerDetails() || {};
      const response = await VendorApiService.getVendorDetails(customerId);

      setConversationContext((prevConversationContext) => {
        return {
          ...prevConversationContext,
          userType: UserType.Vendor,
          userId1: loggedInUserInternalId ?? '',
          userId1Name: response?.data?.doingBusinessAs ?? '',
          userId1UserName: loggedInUserName ?? '',
          userId2: selectedChat?.id ?? '',
          userId2Name: selectedChat?.name ?? '',
          userId2UserName: '',
          jobId: jobData?.jobDetails?.jobId,

          userAndJobDetails: {
            jobName: jobData?.jobDetails?.jobName ?? '',
            vendorId: loggedInUserInternalId ?? '',
            jobId: jobData?.jobDetails?.jobId,
            vendorUserName: loggedInUserName ?? '',
            vendorCompanyName: response?.data?.doingBusinessAs ?? '',
            userType: UserType.Vendor,
            facilityName: '',
            conversationGroupName: '',
            associateId: selectedChat?.id ?? '',
            associateName: selectedChat?.name ?? '',
            customerOrDmSubVendorId: '',
            customerOrDmOrSubVendorUserName: '',
            customerOrDmOrSubVendorCompanyName: '',
          },
          primaryContactName: t('common:chatWithDM'),
        };
      });
      setLoadingMembers((prev) => ({
        ...prev,
        [selectedChat.id as string]: false,
      }));
    } catch (error) {
      console.error('Error fetching vendor details or setting conversation context:', error);
    }
  }, []);

  useEffect(() => {
    if (!isEmpty(conversationContext)) {
      handleConversationIconClick();
    }
  }, [conversationContext]);

  const renderTeamContent = (teamData: ITeamMemberData[]) => {
    return (
      <>
        {teamData?.length > 0 && (
          <AssignTeamDiv>
            {teamData?.map((member: ITeamMemberData) => {
              if (!member.id) return null;
              return (
                <>
                  <TeamCard
                    key={member.id}
                    onClick={() => {
                      if (!member.isAlreadyChecked) {
                        handleSelectMember(
                          member.id as string,
                          assignmentType === AssignmentCardType.AssignSupervisor ? 'supervisor' : 'associate'
                        );
                      }
                    }}>
                    <div style={{ display: 'flex' }}>
                      <LogoWrapper>
                        <div>{getAcronymName(member.name as string).toUpperCase()}</div>
                      </LogoWrapper>
                      <TeamContainer>
                        <div className="Title">{member.name}</div>
                        <div className="Position">{convertToReadableFormat(member.role)}</div>
                      </TeamContainer>
                    </div>
                    <div>
                      <Box sx={{ marginLeft: '1rem' }}>
                        {assignmentType === AssignmentCardType.AssignAssociate && member.isAlreadyChecked ? (
                          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
                            {hasAccessToConversations() &&
                              loggedInUserInternalId !== member?.id &&
                              (loadingMembers[member.id] ? (
                                <CircularProgress
                                  size={24}
                                  sx={{
                                    color: theme.palette.primary.dark,
                                  }}
                                />
                              ) : (
                                <img
                                  src={Icons.GreenConversationIcon}
                                  alt={t('altTexts:conversations')}
                                  style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                                  onClick={() => setDataForChat(member)}
                                />
                              ))}
                            <img
                              src={Icons.EditBlackIcon}
                              alt={t('altTexts:edit')}
                              style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                              onClick={() => {
                                setTeamCardNumber(teamCardNumber + 1);
                                setEditAssociateShiftDate({
                                  userId: member?.id ?? '',
                                  associateStartDate: member?.associateStartDate ?? '',
                                  associateEndDate: member?.associateEndDate ?? '',
                                });
                              }}
                            />
                            <img
                              src={Icons.DeleteIcon}
                              alt={t('altTexts:delete')}
                              style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenModal(member);
                              }}
                            />
                          </Box>
                        ) : member.isAlreadyChecked ? (
                          <Box sx={{ display: 'flex', flexDirection: 'row', gap: '0.5rem' }}>
                            {hasAccessToConversations() &&
                              loggedInUserInternalId !== member?.id &&
                              (loadingMembers[member.id] ? (
                                <CircularProgress
                                  size={24}
                                  sx={{
                                    color: theme.palette.primary.dark,
                                  }}
                                />
                              ) : (
                                <img
                                  src={Icons.GreenConversationIcon}
                                  alt={t('altTexts:conversations')}
                                  style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                                  onClick={() => {
                                    void setDataForChat(member);
                                  }}
                                />
                              ))}
                            <img
                              src={Icons.DeleteIcon}
                              alt={t('altTexts:delete')}
                              style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenModal(member);
                              }}
                            />
                          </Box>
                        ) : member.isChecked ? (
                          <img
                            src={Icons.CheckedIcon}
                            alt={t('altTexts:checked')}
                            style={{ cursor: 'pointer', height: '1.5rem', width: '1.5rem' }}
                          />
                        ) : (
                          <img src={Icons.Unchecked} alt={t('altTexts:unchecked')} style={{ cursor: 'pointer' }} />
                        )}
                      </Box>
                    </div>
                  </TeamCard>
                </>
              );
            })}
          </AssignTeamDiv>
        )}
      </>
    );
  };

  const disableNextButton = () => {
    if (assignmentType === AssignmentCardType.AssignSupervisor) {
      return checkedSupervisors?.length ? false : true;
    } else {
      return checkedAssociates?.length ? false : true;
    }
  };

  const handleAddAssociates = async () => {
    setIsConfirmButtonLoading(true);
    try {
      const customerDetails = getStoredCustomerDetails();
      const vendorId = customerDetails.customerId;
      const startDate = getValues(FieldNames.startDate);
      const endDate = getValues(FieldNames.endDate);
      if (checkedAssociates.length) {
        const newlyAddedAssociates = checkedAssociates.filter((item) => !item.isAlreadyChecked && item.isChecked);
        const editTeamMembers = checkedAssociates.filter((data) => data?.id === editAssociateShiftDate?.userId);
        let finalMembers;
        if (isEmpty(editAssociateShiftDate?.userId)) {
          finalMembers = newlyAddedAssociates;
        } else {
          finalMembers = editTeamMembers;
        }
        const createWorkOrderTeamInput: CreateWorkOrderTeam[] = finalMembers.map((item) => {
          const selectedStartDate = moment(moment(startDate).format('YYYY-MM-DDT00:00:00.000')).format('YYYY-MM-DD');
          const selectedEndDate = moment(moment(endDate).format('YYYY-MM-DDT00:00:00.000')).format('YYYY-MM-DD');
          if (item.teamId) {
            return {
              userId: item.id || '',
              vendorId,
              roleId: item.roleId as string,
              isSupervisor: item.isSupervisor,
              teamId: item.teamId,
              associateStartDate: selectedStartDate,
              associateEndDate: selectedEndDate,
            };
          }
          const newMemberInput: CreateWorkOrderTeam = {
            userId: item.id || '',
            vendorId,
            roleId: item.roleId as string,
            isSupervisor: item.isSupervisor,
          };
          if (!item.isSupervisor) {
            newMemberInput.associateStartDate = selectedStartDate;
            newMemberInput.associateEndDate = selectedEndDate;
          }
          return newMemberInput;
        });
        const confirmedTeamResponse = isEmpty(editAssociateShiftDate?.userId)
          ? await WorkOrdersApiService.addTeamAssignmentWorkOrderTeam({
              workOrderId,
              createScheduleAssignmentsInput: createWorkOrderTeamInput,
              woScheduleMasterId: shiftDetailId,
              // SP self-perform date-picked schedules: send the calendar selections so the backend
              // creates shifts only on those dates.
              ...(customDates && customDates?.length > 0 ? { scheduleType: 'Custom', customDates } : {}),
            })
          : await WorkOrdersApiService.updateTeamAssignmentWorkOrderTeam({
              workOrderId,
              updateScheduleAssignmentsInput: createWorkOrderTeamInput,
            });
        if (confirmedTeamResponse.errors?.length) {
          setSnackBarStyle(true);
          setSnackbarMessage(confirmedTeamResponse.errors[0].message as SetStateAction<string>);
          setSnackbarSeverity('error');
          setSnackbarOpen(true);
        } else {
          setSnackBarStyle(true);
          setSnackbarMessage(`${t('messages:successfullyAssignedAssociate')}`);
          setSnackbarSeverity('success');
          setSnackbarOpen(true);
          setTimeout(() => {
            onClose(assignmentType);
          }, 1000);
          await fetchScheduleDetails(workOrderData?.workOrderId as string);
        }
      }
      setIsConfirmButtonLoading(false);
      setSnackBarStyle(false);
    } catch (e) {
      setIsConfirmButtonLoading(false);
      console.error('Error', e);
      setSnackBarStyle(false);
    }
  };

  useEffect(() => {
    disableNextButton();
  }, [teamCardNumber, checkedSupervisors, checkedAssociates]);

  const handleChange = (event: any, value: number): void => {
    setCurrentPage(value);
  };

  const handlePaginationChange = (event: any, value: number, searchString?: string) => {
    handleChange(event, value);
    fetchOrganizationUsers?.(value, searchString || '');
  };

  return (
    <>
      <HeadingText>
        {teamCardNumber === 1
          ? t('assignJob:assignDuration')
          : assignmentType === AssignmentCardType.AssignSupervisor
            ? t('assignJob:assignSupervisors')
            : t('assignJob:assignAssociates')}
      </HeadingText>
      <Divider sx={divider} />
      {teamCardNumber === 1 ? (
        <Box sx={{ display: 'flex' }}>
          <DatePickerContainer>
            <DateSelection
              requiredText={t('oneTimeJob:startDateFieldReq')}
              control={control}
              fieldName={FieldNames.startDate}
              label={t('calendar:startDate')}
              iconColor="black"
              isExcludePastDate={false}
              showSpecificRangeDates={{
                from:
                  selectedShift?.[0]?.scheduleStartDate &&
                  formatDate(selectedShift?.[0]?.scheduleStartDate as string | undefined),
                to:
                  selectedShift?.[0]?.scheduleEndDate &&
                  formatDate(selectedShift?.[0]?.scheduleEndDate as string | undefined),
              }}
            />
            <DateSelection
              requiredText={t('oneTimeJob:endDateFieldReq')}
              control={control}
              fieldName={FieldNames.endDate}
              label={t('calendar:endDate')}
              iconColor="black"
              isExcludePastDate={false}
              showSpecificRangeDates={{
                from:
                  selectedShift?.[0]?.scheduleStartDate &&
                  formatDate(selectedShift?.[0]?.scheduleStartDate as string | undefined),
                to:
                  selectedShift?.[0]?.scheduleEndDate &&
                  formatDate(selectedShift?.[0]?.scheduleEndDate as string | undefined),
              }}
            />
          </DatePickerContainer>
        </Box>
      ) : (
        <>
          <CardTitle>
            {assignmentType === AssignmentCardType.AssignSupervisor
              ? t('assignJob:selectSupervisor')
              : t('assignJob:selectAssociateBelow')}
            :
          </CardTitle>
          <SearchFieldContainer
            sx={{
              boxShadow: '0px 10px 20px 6px rgba(121, 87, 0, 0.10)',
              marginBottom: '2em',
            }}>
            <SearchInput
              type="text"
              placeholder={
                assignmentType === AssignmentCardType.AssignSupervisor
                  ? t('assignJob:searchSupervisor')
                  : t('assignJob:searchAssociate')
              }
              onChange={(event) => {
                handlePaginationChange({}, 1, event?.target?.value?.trim() ?? '');
                if (!isEmpty(event?.target?.value?.trim())) {
                  setIsRecommended(false);
                }
                {
                  setIsRecommended(true);
                }
              }}
            />
            <img src={Icons.SearchIcon} alt={t('altTexts:search')} style={{ width: '1.5rem', height: '1.5rem' }} />
          </SearchFieldContainer>
          {isRecommended ? <CardTitle>{t('assignJob:recommended')}</CardTitle> : null}
          <CardContainerWrapper>
            {isTeamDataLoading ? (
              <CircularProgress size={30} sx={{ color: theme.palette.primary.dark, marginLeft: '45%' }} />
            ) : assignmentType === AssignmentCardType.AssignSupervisor ? (
              teamData?.length > 0 ? (
                renderTeamContent(teamData)
              ) : (
                <Typography sx={{ display: 'flex', justifyContent: 'center' }}>
                  {t('assignJob:noResultsFound')}
                </Typography>
              )
            ) : associateTeamData?.length > 0 ? (
              renderTeamContent(associateTeamData)
            ) : (
              <Typography sx={{ display: 'flex', justifyContent: 'center' }}>
                {t('assignJob:noResultsFound')}
              </Typography>
            )}
          </CardContainerWrapper>
        </>
      )}
      {teamCardNumber === 1 ? (
        <div style={{ display: 'flex' }}>
          <Button
            // disabled={disableNextButton()}
            sx={{ ...cancelButton, cursor: 'pointer', maxWidth: '100%', marginTop: '2em', width: '100%' }}
            onClick={() => onClose(assignmentType)}>
            <Typography sx={{ ...buttonText, color: theme.palette.primary.dark }}>{t('assignJob:cancel')}</Typography>
          </Button>
          <Button
            // disabled={disableNextButton()}
            sx={{ ...createJobButton, maxWidth: '100%', marginTop: '2em', width: '100%' }}
            onClick={handleAddAssociates}>
            {isConfirmButtonLoading ? (
              <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
            ) : (
              <Typography sx={{ ...buttonText, color: theme.palette.common.white }}>
                {t('assignJob:confirm')}
              </Typography>
            )}
          </Button>
        </div>
      ) : (
        <Button
          disabled={disableNextButton()}
          sx={{ ...createJobButton, opacity: disableNextButton() ? '0.5' : '1', maxWidth: '100%', marginTop: '2em' }}
          onClick={handleNextButton}>
          {isDataSaving ? (
            <CircularProgress size={30} sx={{ color: theme.palette.common.white }} />
          ) : (
            <Typography sx={{ ...buttonText, color: theme.palette.common.white }}>
              {assignmentType === AssignmentCardType.AssignSupervisor
                ? t('assignJob:assignSupervisors')
                : t('assignJob:next')}
            </Typography>
          )}
        </Button>
      )}
      {teamCardNumber !== 1 && (
        <Box sx={{ justifyContent: 'space-between', display: 'flex' }}>
          {!isTeamDataLoading && totalCount && totalCount > NUMBER_OF_ITEMS_PER_PAGE && (
            <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
              <StyledPagination
                count={Math.ceil(totalCount / NUMBER_OF_ITEMS_PER_PAGE)}
                page={currentPage}
                onChange={handlePaginationChange}
                color="primary"
              />
            </Stack>
          )}
        </Box>
      )}
      <ModifyPopUp
        open={isModalOpen}
        handleClose={handleCloseModal}
        handleOnCloseJob={() => handleDeleteTeam(deleteMember)}
        loading={isLoading}
        heading={
          assignmentType === AssignmentCardType.AssignSupervisor
            ? t('assignJob:removeSupervisor')
            : t('assignJob:removeAssociate')
        }
        text={
          assignmentType === AssignmentCardType.AssignSupervisor
            ? t('assignJob:areYouSureToRemoveSupervisor')
            : t('assignJob:areYouSureToRemoveAssociate')
        }
      />

      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
        styles={snackbarStyle && { width: '100%', margin: 0 }}
      />
    </>
  );
};

export default AssignTeam;
